import {
  type ComponentPropsWithoutRef,
  type ComponentRef,
  forwardRef,
  useEffect,
  useState,
} from "react";

import * as Slider from "@radix-ui/react-slider";
import { type UseRangeProps, useRange } from "react-instantsearch";

import { numFormat } from "@/shared.utils/number-format";

interface SliderThumbProps {
  value: number;
}
type SliderThumbRef = ComponentRef<typeof Slider.Thumb>;
const SliderThumb = forwardRef<
  SliderThumbRef,
  SliderThumbProps
>((props, ref) => {
  const { value } = props;

  return (
    <Slider.Thumb
      ref={ref}
      className={`
        block size-5 rounded-[10px] bg-accent-4 shadow-2
        relative
        focus:shadow-4 focus:outline-none
        hover:bg-accent-3
      `}
    >
      <span className={`
        absolute inset-0 text-2 text-accent-11 top-8 flex
        items-center justify-center
      `}
      >
        {numFormat(value, { decimal: 0 })}
      </span>
    </Slider.Thumb>
  );
});
SliderThumb.displayName = "SliderThumb";

type SliderProps = ComponentPropsWithoutRef<typeof Slider.Root>;
interface RangeSliderProps extends UseRangeProps, Pick<SliderProps, "step"> {}
type RangeSliderRef = ComponentRef<typeof Slider.Root>;

export const RangeSlider = forwardRef<RangeSliderRef, RangeSliderProps>(
  (props, ref) => {
    const {
      step = 10,
      ...rangeProps
    } = props;

    const { start, range, canRefine, refine } = useRange(rangeProps);

    // min and max slider values
    const {
      min = 0,
      max = 0,
    } = range;
    // user selected values
    const [
      selectedFrom,
      selectedTo,
    ] = start;

    // slider range value
    const [value, setValue] = useState([min, max]);

    const from = Math.max(
      min,
      (selectedFrom && Number.isFinite(selectedFrom)) ? selectedFrom : min,
    );
    const to = Math.min(
      max,
      (selectedTo && Number.isFinite(selectedTo)) ? selectedTo : max,
    );

    useEffect(() => {
      setValue([from, to]);
    }, [from, to]);

    const onValueCommit = (v: number[]) => {
      refine([v[0], v[1]]);
    };

    if (
      (value[0] === value[1])
      && (value[0] === min)
      && (value[0] === max)
    ) {
      return (
        <div className="mb-4 h-8">
          <div className={`
            block size-5 rounded-[10px] bg-accent-4 shadow-2
            relative
            focus:shadow-4 focus:outline-none
            hover:bg-accent-3
          `}
          >
            <span className={`
              absolute inset-0 text-2 text-accent-11 top-8
              flex items-center justify-center
            `}
            >
              {numFormat(value[0], { decimal: 0 })}
            </span>
          </div>
        </div>
      );
    }

    return (
      <Slider.Root
        ref={ref}
        min={min}
        max={max}
        value={value}
        onValueChange={setValue}
        onValueCommit={onValueCommit}
        disabled={!canRefine}
        step={step}
        className={`
          relative mb-4 flex h-8 w-[90%] max-w-[300px]
          touch-none select-none items-center justify-start
        `}
      >
        <Slider.Track className={`
          relative h-[3px] grow rounded-full bg-gray-3
        `}
        >
          <Slider.Range className={`
            absolute h-full rounded-full bg-gray-9
          `}
          />
        </Slider.Track>
        <SliderThumb
          value={value[0]}
        />
        <SliderThumb
          value={value[1]}
        />
      </Slider.Root>
    );
  });
RangeSlider.displayName = "RangeSlider";
